import { useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { Badge } from '@/components/ui/badge'
import { useClientHelpDesk } from '@/queries/clientDashboards'
import { formatDate, formatDayLabel, formatDuration } from '@/lib/dates'
import { areaName } from '@/lib/productAreas'
import { statusLabels } from '@/lib/status'
import { TICKET_TYPES, clientTypeLabel, typeName } from '@/lib/ticketTypes'
import { cn } from '@/lib/utils'
import { TICKET_GROUP_LABELS } from '@/types/clientDashboard'
import type { HelpDeskCounts, HelpDeskHealth, HelpDeskPeriod, HelpDeskTicketRow, TicketGroup } from '@/types/clientDashboard'
import type { TicketStatus } from '@/types/ticket'
import { AreaBars, CountChip, DashCard, Dot, Kpi, Legend, pillClass, sectionLabelClass, tdClass, thClass, Tip } from './dashboardParts'
import { LoadError } from './LoadError'
import { GROUP_COLOURS, typeColour } from './typeColours'

const compactSelectClass =
  'h-8 shrink-0 rounded-etn-pill border border-etn-border-input bg-etn-surface px-3 text-[12.5px] text-etn-text-cell'

const GROUPS: TicketGroup[] = ['queue', 'tracker-help-desk', 'tracker-manual']
const OPEN_STATUSES = ['open', 'in_progress', 'pending']
const NO_TYPE_HELP = 'Not set yet: staff or Investigate can set it'
const MINUTE_MS = 60000
const DAY_MS = 86400000
const OLDEST_OPEN_WARN_DAYS = 14

export const formatMinutes = (minutes: number | null) => (minutes === null ? '-' : formatDuration(new Date(0), new Date(minutes * MINUTE_MS)))

const typeLabel = (key: string | null) => (key ? typeName(key) : 'No type')
const areaLabel = (key: string | null) => (key ? areaName(key) : 'No area')
const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS)
const percent = (count: number, total: number) => `${total ? Math.round((count / total) * 100) : 0}%`

function GroupSplit({ byGroup, field }: { byGroup: Record<TicketGroup, HelpDeskCounts>; field: keyof HelpDeskCounts }) {
  return (
    <div
      className="mt-2 flex gap-3 text-[11.5px] tabular-nums text-etn-text-label"
      aria-label={GROUPS.map(group => `${TICKET_GROUP_LABELS[group]} ${byGroup[group][field]}`).join(', ')}
    >
      {GROUPS.map(group => (
        <Tip key={group} label={TICKET_GROUP_LABELS[group]} value={byGroup[group][field]} colour={GROUP_COLOURS[group]}>
          <span className="inline-flex cursor-default items-center gap-1" aria-label={`${TICKET_GROUP_LABELS[group]} ${byGroup[group][field]}`}>
            <Dot colour={GROUP_COLOURS[group]} />
            {byGroup[group][field]}
          </span>
        </Tip>
      ))}
    </div>
  )
}

// Fill the months between the first ticket and the period's end so quiet months still show.
function monthRows(figures: HelpDeskHealth) {
  const counts = new Map(figures.perMonth.map(month => [month.month, month.byType]))
  const first = figures.perMonth[0]?.month
  if (!first) return []
  const start = dayjs(`${first}-01`)
  const lastData = dayjs(`${figures.perMonth[figures.perMonth.length - 1].month}-01`)
  const end = dayjs(figures.period.to).startOf('month').isAfter(lastData) ? dayjs(figures.period.to).startOf('month') : lastData
  return Array.from({ length: end.diff(start, 'month') + 1 }, (_, offset) => start.add(offset, 'month').format('YYYY-MM')).map(month => ({
    month,
    byType: counts.get(month) ?? {}
  }))
}

function MonthChart({ figures }: { figures: HelpDeskHealth }) {
  const months = monthRows(figures)
  const typeKeys = [...TICKET_TYPES.map(type => type.key), 'none'].filter(key => months.some(month => month.byType[key]))
  const totals = months.map(month => Object.values(month.byType).reduce((sum, count) => sum + count, 0))
  const max = Math.max(1, ...totals)
  if (!months.length) return <p className="text-[12.5px] text-etn-text-soft">Nothing raised in this period.</p>

  return (
    <>
      <Legend items={typeKeys.map(key => ({ key, label: key === 'none' ? 'No type' : typeName(key), colour: typeColour(key === 'none' ? null : key) }))} />
      <div className="mt-3 space-y-2">
        {months.map((month, index) => {
          const date = dayjs(`${month.month}-01`)
          return (
            <div key={month.month} className="flex items-center gap-3" data-testid="month-row" data-month={month.month}>
              <div className="w-16 shrink-0 text-[12.5px]">
                <b className="font-semibold text-etn-text">{date.format('MMM')}</b>
                {(index === 0 || date.month() === 0) && <span className="ml-1 text-[11px] text-etn-text-soft">{date.format('YYYY')}</span>}
              </div>
              <div className="flex flex-1 items-center gap-2">
                <div className="flex h-4 gap-[2px] overflow-hidden rounded" style={{ width: `${(totals[index] / max) * 100}%` }}>
                  {typeKeys
                    .filter(key => month.byType[key])
                    .map(key => {
                      const label = key === 'none' ? 'No type' : typeName(key)
                      const colour = typeColour(key === 'none' ? null : key)
                      return (
                        <Tip key={key} label={label} value={month.byType[key]} colour={colour}>
                          <span aria-label={`${label} ${month.byType[key]}`} style={{ flexGrow: month.byType[key], flexBasis: 0, background: colour }} />
                        </Tip>
                      )
                    })}
                </div>
                <span className="text-xs tabular-nums text-etn-text-soft">{totals[index]}</span>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

function StatusCell({ ticket }: { ticket: HelpDeskTicketRow }) {
  if (ticket.completedAt) return <span className="text-etn-text-soft">Closed in {formatDuration(ticket.createdAt, ticket.completedAt)}</span>
  if (OPEN_STATUSES.includes(ticket.status)) return <Badge variant="warning">Open · {daysSince(ticket.createdAt)}d</Badge>
  if (ticket.status === 'on_hold') return <Badge variant="neutral">On hold · {daysSince(ticket.createdAt)}d</Badge>
  return <span className="text-etn-text-soft">{statusLabels[ticket.status as TicketStatus] ?? ticket.status}</span>
}

export function HelpDeskHealthView({ companyId }: { companyId: string }) {
  const [period, setPeriod] = useState<HelpDeskPeriod>('12m')
  const [group, setGroup] = useState<TicketGroup | ''>('')
  const [type, setType] = useState('')
  const [area, setArea] = useState('')
  const [status, setStatus] = useState('open')
  const listRef = useRef<HTMLDivElement>(null)
  const { data, isLoading, isError, refetch } = useClientHelpDesk(companyId, period)
  // The area and status options are rebuilt from the new period's tickets, so a kept value could match none of them.
  const changePeriod = (next: HelpDeskPeriod) => {
    setPeriod(next)
    setArea('')
    setStatus('open')
  }

  if (isError && !data) return <LoadError title="Help desk figures didn't load" onRetry={() => refetch()} />
  if (isLoading || !data) return null
  const figures = data.data
  const { totals } = figures

  const typeRows = [...figures.byType].sort((left, right) => right.count - left.count)
  const areaKeys = [...new Set(figures.tickets.map(ticket => ticket.productArea ?? ''))]
  const otherStatuses = [...new Set(figures.tickets.map(ticket => ticket.status))].filter(key => !OPEN_STATUSES.includes(key))
  const matchesStatus = (ticket: HelpDeskTicketRow) => !status || (status === 'open' ? OPEN_STATUSES.includes(ticket.status) : ticket.status === status)
  const filtered = figures.tickets.filter(
    ticket => (!group || ticket.group === group) && (!area || (ticket.productArea ?? 'none') === area) && matchesStatus(ticket)
  )
  const visible = filtered.filter(ticket => !type || (ticket.type ?? 'none') === type)
  const exampleOf = (key: string | null) => figures.tickets.find(ticket => ticket.type === key)?.subject
  const chooseType = (key: string) => {
    setType(key)
    listRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }
  const pills = [
    { key: '', label: 'All', count: filtered.length },
    ...typeRows.map(row => ({ key: row.type ?? 'none', label: typeLabel(row.type), count: filtered.filter(ticket => (ticket.type ?? 'none') === (row.type ?? 'none')).length }))
  ].filter(pill => !pill.key || pill.count || pill.key === type)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <span className={sectionLabelClass}>Totals</span>
          <Legend items={GROUPS.map(key => ({ key, label: TICKET_GROUP_LABELS[key], colour: GROUP_COLOURS[key] }))} />
        </div>
        <select aria-label="Period" className={compactSelectClass} value={period} onChange={e => changePeriod(e.target.value as HelpDeskPeriod)}>
          <option value="3m">Last 3 months</option>
          <option value="12m">Last 12 months</option>
          <option value="all">All time</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi testId="figure-raised" label="Total raised" value={totals.raised} detail={`${formatDate(figures.period.from)} to ${formatDate(figures.period.to)}`}>
          <GroupSplit byGroup={figures.byGroup} field="raised" />
        </Kpi>
        <Kpi testId="figure-open" label="Open right now" value={totals.open} tone={totals.open ? 'warn' : undefined} detail={totals.open ? 'Waiting on us' : 'Nothing open'}>
          <GroupSplit byGroup={figures.byGroup} field="open" />
        </Kpi>
        <Kpi testId="figure-on-hold" label="On hold" value={totals.onHold} detail={totals.onHold ? 'Paused, not closed' : 'None on hold'}>
          <GroupSplit byGroup={figures.byGroup} field="onHold" />
        </Kpi>
        <Kpi testId="figure-completed" label="Completed" value={totals.completed} tone="good" detail={`${percent(totals.completed, totals.raised)} of what they raised`}>
          <GroupSplit byGroup={figures.byGroup} field="completed" />
        </Kpi>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi testId="figure-first-reply" label="Median time to first reply (clock time, chosen period)" value={formatMinutes(figures.medianFirstReplyMinutes)} detail="Tickets the client raised" />
        <Kpi testId="figure-complete" label="Median time to complete (clock time, chosen period)" value={formatMinutes(figures.medianCompleteMinutes)} detail={`Across ${totals.completed} completed`} />
        <Kpi
          testId="figure-oldest-open"
          label="Oldest open"
          value={figures.oldestOpen ? `${daysSince(figures.oldestOpen.createdAt)}d` : '-'}
          tone={figures.oldestOpen && daysSince(figures.oldestOpen.createdAt) > OLDEST_OPEN_WARN_DAYS ? 'warn' : undefined}
          detail={figures.oldestOpen ? <span>{figures.oldestOpen.subject}</span> : 'Nothing open'}
        />
        <Kpi testId="figure-tracker" label="Tracker items" value={figures.tracker.total} detail={`${figures.tracker.completed} completed`} />
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <DashCard title="Raised each month" subtitle="By type">
            <div className="px-[18px] py-3.5">
              <MonthChart figures={figures} />
            </div>
          </DashCard>

          <DashCard title="What they ask about" subtitle="Darker part is information requests" testId="by-area">
            <div className="px-[18px] py-3.5">
              <AreaBars areas={figures.byArea} labelOf={areaName} />
            </div>
          </DashCard>
        </div>

        <DashCard title="By type" subtitle="Select a type to filter the list below" testId="by-type">
          <ul>
            {typeRows.map(row => {
              const example = exampleOf(row.type)
              return (
                <li key={row.type ?? 'none'} className="border-t border-etn-border-row first:border-t-0">
                  <button
                    type="button"
                    onClick={() => chooseType(row.type ?? 'none')}
                    className={cn('flex w-full items-center gap-3 px-[18px] py-3 text-left hover:bg-etn-row-hover', type === (row.type ?? 'none') && 'bg-etn-row-hover')}
                  >
                    <Dot colour={typeColour(row.type)} size={10} />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-etn-text">{typeLabel(row.type)}</span>
                      <span className="mt-0.5 block text-[12.5px] leading-normal text-etn-text-label">
                        {row.type ? clientTypeLabel(row.type) : NO_TYPE_HELP}
                        {example && ` · e.g. "${example}"`}
                      </span>
                    </span>
                    <span className="text-xs tabular-nums text-etn-text-soft">{percent(row.count, totals.raised)}</span>
                    <b className="w-6 text-right text-base font-semibold tabular-nums text-etn-text">{row.count}</b>
                  </button>
                </li>
              )
            })}
          </ul>
        </DashCard>
      </div>

      <div ref={listRef} className="scroll-mt-4">
        <DashCard
          title="Their help desk tickets"
          subtitle={`Newest first · ${visible.length} shown`}
          right={
            <div className="flex flex-wrap gap-1.5">
              {pills.map(pill => (
                <button
                  key={pill.key || 'all'}
                  type="button"
                  onClick={() => setType(pill.key)}
                  className={pillClass(type === pill.key)}
                >
                  <span>{pill.label}</span> <CountChip>{pill.count}</CountChip>
                </button>
              ))}
            </div>
          }
        >
          <div className="flex flex-wrap items-center gap-2 border-b border-[var(--etn-gray-100)] px-[18px] py-2.5">
            <select aria-label="Filter by group" className={compactSelectClass} value={group} onChange={e => setGroup(e.target.value as TicketGroup | '')}>
              <option value="">All groups</option>
              {GROUPS.map(key => <option key={key} value={key}>{TICKET_GROUP_LABELS[key]}</option>)}
            </select>
            <select aria-label="Filter by area" className={compactSelectClass} value={area} onChange={e => setArea(e.target.value)}>
              <option value="">All areas</option>
              {areaKeys.map(key => <option key={key || 'none'} value={key || 'none'}>{areaLabel(key || null)}</option>)}
            </select>
            <select aria-label="Filter by status" className={compactSelectClass} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="open">Open</option>
              {otherStatuses.map(key => <option key={key} value={key}>{statusLabels[key as TicketStatus] ?? key}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['Raised', 'Ticket', 'Type', 'Area', 'From'].map(heading => (
                    <th key={heading} scope="col" className={thClass}>{heading}</th>
                  ))}
                  <th scope="col" className={`${thClass} text-right`}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-etn-border-row">
                {visible.map(ticket => (
                  <tr key={ticket._id} data-testid="help-desk-ticket-row" className="hover:bg-etn-row-hover">
                    <td className={`${tdClass} whitespace-nowrap text-etn-text-soft`}>{formatDayLabel(ticket.createdAt)}</td>
                    <td className={`${tdClass} min-w-[240px]`}>
                      <Link to={`/admin/tickets/${ticket._id}` as never} className="font-medium text-etn-text hover:text-etn-accent">{ticket.subject}</Link>
                      {ticket.group !== 'queue' && (
                        <span className="ml-2 inline-flex items-center gap-1 whitespace-nowrap text-[11.5px] text-etn-text-label">
                          <Dot colour={GROUP_COLOURS[ticket.group]} size={6} />
                          {TICKET_GROUP_LABELS[ticket.group]}
                        </span>
                      )}
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      <span className="inline-flex items-center gap-1.5 font-medium text-etn-text-cell">
                        <Dot colour={typeColour(ticket.type)} />
                        {typeLabel(ticket.type)}
                      </span>
                    </td>
                    <td className={`${tdClass} whitespace-nowrap text-etn-text-soft`}>{areaLabel(ticket.productArea)}</td>
                    <td className={`${tdClass} whitespace-nowrap text-etn-text-soft`}>{ticket.requesterName ?? '-'}</td>
                    <td className={`${tdClass} whitespace-nowrap text-right`}><StatusCell ticket={ticket} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!visible.length && <p className="px-[18px] py-8 text-center text-[13px] text-etn-text-soft">No tickets match these filters.</p>}
          </div>
        </DashCard>
      </div>
    </div>
  )
}
